import React, { useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { View } from 'react-native';
import Button from 'design-system/Button';
import Logo from 'design-system/Logo';
import StatusBar from 'design-system/StatusBar';
import Text from 'design-system/Text';
import { rem } from 'design-system/values';
import { createAccount, editAccount, getAccountGoals, getTransactions } from 'actions';
import { muteErrorAndFetching, useFetchOld } from 'hooks/useFetch';
import useRefreshAccounts from 'hooks/useRefreshAccounts';
import { getSocialMediaData } from 'actions/socialMedia';
import { AppView } from 'design-system/AppView';
import { ButtonGroup } from 'design-system/ButtonGroup';
import CardRow from 'design-system/CardRow';
import { CardView } from 'design-system/CardView';
import { Spacer } from 'design-system/Spacer';
import Squircle from 'design-system/Squircle';
import { useRequiredBenefitCallbackWithSpaces } from 'features/premium/hooks/useRequiredBenefitCallback';
import useColors from 'hooks/useColors';
import Modal from 'utils/packages/Modal';
import useStyles from 'hooks/useStyles';
import { selectAccountById } from 'reducers/selectors';
import { selectShowNewPhysicalAssets } from 'features/accounts/selectors';
import { useAppDispatch, useAppSelector, useAppStore } from 'store/hooks';
import { getBalanceInNativeCurrency } from 'utils/accounts';
import Amplitude from 'utils/amplitude';
import createStyleSheets from 'utils/createStyleSheets';
import { formatAmount } from 'utils/formatting';
import { goBackWithKey } from 'utils/navigationv6';
import { useSmartRulesNavigation } from 'hooks/useSmartRulesNavigation';
import { getSpaceInfo, getSpaceInfoSuccess } from 'features/spaces/actions';
import InfoCard from 'design-system/InfoCard';
import { Avatar } from 'design-system/avatar';
import LogoBankAccount from 'design-system/LogoBankAccount';
import IconCircle from 'design-system/icons/IconCircle';
import EmojiText from 'design-system/EmojiText';
import useValuationChange from 'features/transactions/hooks/useValuationChange';
import { format, parseISO } from 'date-fns';
import { isWeb } from '../../../constants';
import { DUMMY_ACCOUNT_TEXT_MAP, getPropertyName, PHYSICAL_ASSET_TYPES } from '../constants';
import useDummyAccountLogo from '../hooks/useDummyAccountLogo';
import useInvestmentGraphBalanceHistory from '../hooks/useInvestmentGraphBalanceHistory';
import { useUploadCsvTransactions } from '../hooks/useUploadCsvTransactions';
const AVATAR_SIZE = 44;
const BORDER_WIDTH = 1;
const strings = {
    confirmOfflineAccount: 'Confirm offline account',
    changeBalance: 'Change balance',
    confirmAsset: 'Continue',
    goBack: 'Go back',
    error: {
        importError: {
            title: 'Something went wrong',
            description: 'We were unable to import your transactions. Please try again.',
            buttonText: 'Ok',
        },
    },
};
const DummyAccountCreatedScreen = ({ navigation, route }) => {
    const colors = useColors();
    const [, , , fetch] = useFetchOld({ throws: true });
    const userCurrency = useAppSelector((store) => store.user.user.currency);
    const account = useAppSelector((store) => selectAccountById(store, route.params.id));
    const { applyTransactions } = useUploadCsvTransactions();
    const name = route.params.name || account?.name;
    const type = route.params.type || account?.type;
    const balance = route.params.balance !== undefined ? route.params.balance : undefined;
    const currency = route.params.currency || account?.currency;
    const { emoji, iconProvider, iconProviderHandle } = route.params;
    const showNewPhysicalAssets = useAppSelector(selectShowNewPhysicalAssets);
    const isPhysicalAsset = useMemo(() => PHYSICAL_ASSET_TYPES.has(type), [type]);
    useLayoutEffect(() => {
        let title = 'Offline account';
        if (isPhysicalAsset && showNewPhysicalAssets && type) {
            title = getPropertyName(type);
        }
        navigation.setOptions({
            headerTintColor: colors.text.primary,
            title,
        });
    }, [colors.text.primary, isPhysicalAsset, showNewPhysicalAssets, type]);
    const [twitterData, , , fetchTwitter] = useFetchOld(muteErrorAndFetching);
    useEffect(() => {
        if (iconProvider === 'TWITTER' && iconProviderHandle) {
            fetchTwitter(getSocialMediaData(iconProvider, iconProviderHandle));
        }
    }, [fetchTwitter, iconProvider, iconProviderHandle]);
    const logoFallback = useDummyAccountLogo(type, 24);
    const [loading, setLoading] = useState(false);
    const refreshAccounts = useRefreshAccounts();
    const dispatch = useAppDispatch();
    const store = useAppStore();
    const isCsvImport = useMemo(() => route.params.filename && route.params.config, [route.params.filename, route.params.config]);
    const { onPressSmartRules } = useSmartRulesNavigation();
    const onGoBack = useCallback((goToPreviousRoute) => {
        if (route.params.keyBack && goBackWithKey(navigation, route.params.keyBack, goToPreviousRoute)) {
            return;
        }
        if (route.params.id !== undefined) {
            navigation.navigateDeprecated('Transactions', { id: route.params.id });
        }
    }, [route.params.id, route.params.keyBack]);
    const { createValuationChange } = useValuationChange();
    const loadInvestmentGraphBalanceHistory = useInvestmentGraphBalanceHistory();
    const onSubmitCallback = useCallback(async () => {
        const { id } = route.params;
        const body = {};
        if (route.params.currency)
            body.currency = currency;
        if (route.params.type)
            body.type = type;
        if (route.params.emoji)
            body.emoji = emoji;
        if (route.params.iconProvider) {
            body.iconProvider = iconProvider;
            body.iconProviderHandle = iconProviderHandle;
        }
        if (balance !== undefined)
            body.balance = balance;
        if (id) {
            if (route.params.name) {
                body.customName = name;
            }
            try {
                setLoading(true);
                await fetch(editAccount(id, body));
                setLoading(false);
                if (body.balance !== undefined && store.getState().accounts.goals[Number(id)]) {
                    fetch(getAccountGoals());
                }
                if (type) {
                    refreshAccounts(type);
                    if (type === 'INVESTMENT') {
                        loadInvestmentGraphBalanceHistory(id);
                    }
                }
                fetch(getTransactions({ data: Number(id), page: 1, hasInternalTransactions: false }));
                Amplitude.logEvent('Premium.DummyAccountUpdated');
                onGoBack();
            }
            catch {
                // error will be shown by fetch
                setLoading(false);
            }
        }
        else {
            if (route.params.name) {
                body.name = name;
            }
            if (route.params.purchaseDate) {
                body.balanceBookingDate = route.params.purchaseDate;
            }
            let valueChangeAmount = 0;
            if (route.params.initialValue) {
                body.balance = route.params.initialValue;
                valueChangeAmount = (route.params.balance ?? 0) - (route.params.initialValue ?? 0);
            }
            try {
                const { spaceId, filename, config } = route.params;
                setLoading(true);
                const account = await fetch(createAccount(body, spaceId));
                const { id: accountId } = account;
                if (valueChangeAmount && isPhysicalAsset && showNewPhysicalAssets) {
                    await createValuationChange(new Date().toISOString(), account, valueChangeAmount, undefined, true);
                }
                if (isCsvImport && filename && config) {
                    const response = await applyTransactions(accountId, filename, config, true); // true = mute alert
                    if (!response || response.error.length > 0) {
                        throw new Error('Error importing transactions');
                    }
                }
                if (isCsvImport) {
                    await fetch(getAccountGoals());
                }
                if (spaceId)
                    await fetch(getSpaceInfo(spaceId))
                        .then((result) => {
                        dispatch(getSpaceInfoSuccess(result));
                    })
                        .catch(() => { });
                if (type) {
                    refreshAccounts(type);
                    if (type === 'INVESTMENT' || (isPhysicalAsset && showNewPhysicalAssets)) {
                        loadInvestmentGraphBalanceHistory(accountId);
                    }
                }
                if (isCsvImport) {
                    await fetch(getTransactions({ data: Number(accountId), page: 1, hasInternalTransactions: false }));
                }
                const hasSetUpEmmaSelectAssetOrDebtRoute = !!navigation
                    .getState()
                    .routes.find((route) => route.name === 'SetUpEmmaSelectAssetOrDebt');
                setLoading(false);
                Amplitude.logEvent('Premium.DummyAccountCreated');
                const isSmartRuleFlow = route.params.keyBack?.startsWith('SmartRulePickOfflineAccount');
                if (isSmartRuleFlow) {
                    navigation.navigateDeprecated('Success', {
                        title: 'Offline account has been created!',
                        subTitle: '',
                        buttonText: 'Close',
                        onContinue: () => {
                            onGoBack(false);
                            return true;
                        },
                    });
                }
                else if (isPhysicalAsset && showNewPhysicalAssets) {
                    const propertyName = getPropertyName(type);
                    navigation.navigateDeprecated('Success', {
                        title: `${propertyName} created!`,
                        subTitle: `This ${propertyName.toLowerCase()} is now part of your net worth. Update its value anytime to keep your net worth accurate.`,
                        buttonText: 'Done',
                        onContinue: () => {
                            onGoBack();
                            if (hasSetUpEmmaSelectAssetOrDebtRoute) {
                                navigation.navigate('SetUpEmmaAddAnotherBank', { isFromAddAssetOrDebtFlow: true });
                            }
                            return true;
                        },
                    });
                }
                else {
                    navigation.navigateDeprecated('Success', {
                        title: 'Offline account has been created!',
                        subTitle: 'If you set a smart rule, Emma can update the balance by detecting transactions from your linked account.',
                        secondaryButtonText: hasSetUpEmmaSelectAssetOrDebtRoute ? 'Save' : 'Close',
                        onSecondaryButtonPress: () => {
                            onGoBack();
                            if (hasSetUpEmmaSelectAssetOrDebtRoute) {
                                navigation.navigate('SetUpEmmaAddAnotherBank', { isFromAddAssetOrDebtFlow: true });
                            }
                            return true;
                        },
                        buttonText: 'Set a smart rule',
                        onContinue: () => {
                            onGoBack();
                            onPressSmartRules(true);
                            return true;
                        },
                    });
                }
            }
            catch (error) {
                if (error instanceof Error && error.message === 'Error importing transactions') {
                    Modal.showConfirmation({
                        title: strings.error.importError.title,
                        subtitle: strings.error.importError.description,
                        confirmButtonTitle: strings.error.importError.buttonText,
                    });
                }
                setLoading(false);
            }
        }
    }, [
        route.params,
        currency,
        type,
        emoji,
        balance,
        iconProvider,
        iconProviderHandle,
        name,
        onGoBack,
        refreshAccounts,
        loadInvestmentGraphBalanceHistory,
        isPhysicalAsset,
        showNewPhysicalAssets,
        isCsvImport,
        createValuationChange,
        applyTransactions,
        onPressSmartRules,
    ]);
    const onSubmit = useRequiredBenefitCallbackWithSpaces(onSubmitCallback, 'offlineAccounts');
    const { isChangingBalance } = route.params;
    const [title, buttonText, displayBalance] = useMemo(() => {
        let buttonText = isChangingBalance ? strings.changeBalance : strings.confirmOfflineAccount;
        if (isPhysicalAsset && showNewPhysicalAssets) {
            buttonText = strings.confirmAsset;
        }
        return [
            type !== 'CRYPTO' ? DUMMY_ACCOUNT_TEXT_MAP[type] || '' : '',
            buttonText,
            getBalanceInNativeCurrency(account, userCurrency, balance, currency),
        ];
    }, [type, account, userCurrency, balance, currency, isPhysicalAsset, showNewPhysicalAssets, isChangingBalance]);
    const styles = useStyles(styleSet);
    const logo = useMemo(() => {
        const { isChangingLogo, isChangingName, name: nameFromRoute } = route.params;
        if (account && !isChangingLogo) {
            const accountData = isChangingName ? { ...account, name: nameFromRoute } : account;
            return <LogoBankAccount size="R44" data={accountData} onCard/>;
        }
        if (iconProvider === 'TWITTER' && twitterData?.iconUrl) {
            return (<Squircle height={AVATAR_SIZE} width={AVATAR_SIZE} color={colors.cards.onLight}>
          <Logo R44 uri={twitterData.iconUrl}/>
        </Squircle>);
        }
        if (emoji) {
            return (<IconCircle color={colors.background.dark} width={AVATAR_SIZE} height={AVATAR_SIZE}>
          <EmojiText fontSize={20}>{emoji}</EmojiText>
        </IconCircle>);
        }
        const effectiveName = isChangingLogo ? account?.name : nameFromRoute;
        if (effectiveName) {
            return (<Avatar name={effectiveName} color={colors.background.brandLight} size={AVATAR_SIZE} borderWidth={BORDER_WIDTH} fontColor={colors.text.textOnBrand} style={styles.avatar}/>);
        }
        return (<Squircle height={AVATAR_SIZE} width={AVATAR_SIZE} color={colors.cards.onLight}>
        {logoFallback}
      </Squircle>);
    }, [
        route.params,
        account,
        iconProvider,
        twitterData?.iconUrl,
        emoji,
        colors.cards.onLight,
        colors.background.dark,
        colors.background.brandLight,
        colors.text.textOnBrand,
        logoFallback,
        styles.avatar,
    ]);
    const purchaseDateRow = useMemo(() => {
        if (isPhysicalAsset && showNewPhysicalAssets && route.params.purchaseDate) {
            return (<CardRow style={styles.row} small title="Purchase date" info={format(parseISO(route.params.purchaseDate), 'dd MMM yyyy')}/>);
        }
        return null;
    }, [isPhysicalAsset, showNewPhysicalAssets, route.params.purchaseDate, styles.row]);
    const initialValueRow = useMemo(() => {
        if (isPhysicalAsset && showNewPhysicalAssets && route.params.initialValue) {
            return (<CardRow style={styles.row} small title="Initial value" info={<Text Numbers-14>{formatAmount(Number(route.params.initialValue), route.params.currency)}</Text>}/>);
        }
        return null;
    }, [isPhysicalAsset, showNewPhysicalAssets, route.params.initialValue, route.params.currency, styles.row]);
    return (<AppView type="secondary" paddingBottom isInTab={isWeb} avoidHeader>
      <StatusBar barStyle="dark-content" animated/>
      <View style={styles.container}>
        <Spacer h={8}/>
        <CardView withPadding={false}>
          <View style={styles.cardHeader}>
            <Text Text-20>{name}</Text>
            {logo}
          </View>
          <CardRow style={styles.row} small title="Account type" info={title}/>
          {purchaseDateRow}
          {initialValueRow}
          {isCsvImport ? (<CardRow style={styles.row} small numberOfLines={2} title="Currency" info={`${route.params.currencyName} (${route.params.currency})`}/>) : (<CardRow style={styles.row} small title={isPhysicalAsset && showNewPhysicalAssets ? 'Current value' : 'Balance'} info={<Text Numbers-14>{formatAmount(Number(displayBalance.amount), displayBalance.currency, 2)}</Text>}/>)}
        </CardView>
        {!!isCsvImport && (<InfoCard iconSpacing={16}>
            <Text TextThin-14 Secondary>
              Balance comes from your imported transactions and may be off if the file isn’t complete. You can adjust it
              in settings.
            </Text>
          </InfoCard>)}
      </View>
      <ButtonGroup>
        <Button brandReversed title={strings.goBack} onPress={onGoBack}/>
        <Button brand loading={loading} title={buttonText} onPress={onSubmit}/>
      </ButtonGroup>
    </AppView>);
};
const styleSet = createStyleSheets((colors) => ({
    container: {
        flex: 1,
    },
    cardHeader: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: rem(16),
    },
    row: {
        flex: undefined,
    },
    centered: {
        alignSelf: 'center',
    },
    avatar: {
        borderColor: colors.borders.logoStroke,
    },
}));
export default DummyAccountCreatedScreen;
